import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import gameApi, { type AdventureDetail } from '../services/GameApiService';
import AdventureDetailHero from '../components/catalog/AdventureDetailHero';
import AdventureCastList from '../components/catalog/AdventureCastList';
import StartAdventureForm from '../components/catalog/StartAdventureForm';
import LoadingSpinner from '../components/LoadingSpinner';
import logoSvg from '../assets/logo.svg';
import useAdventureMetaTags from '../hooks/useAdventureMetaTags';
import { trackPageVisit } from '../utils/websiteAnalytics';
import { loadSession } from '../utils/sessionPersistence';
import { loadDeviceToken, saveDeviceToken } from '../utils/deviceSession';

/**
 * Página de detalhe de uma aventura (`/adventure/:slug`).
 * Hero + premise + briefing + cast + form de email.
 *
 * The route param may be a slug (`a-setima-arca`) or, for backwards
 * compatibility, a legacy raw document id. We resolve it via the slug endpoint
 * which falls back to id lookup on the backend.
 */
export default function AdventureDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [adventure, setAdventure] = useState<AdventureDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Banner server-backed (F3): se houver cookie com token, chama-se o /resume
  // para obter o progresso actual do run. Só mostramos o banner "Continuar"
  // quando o run do cookie é desta aventura.
  const [resumeSession, setResumeSession] = useState<{
    adventureId: string;
    adventureTitle: string;
    currentEpisode: number;
    totalEpisodes: number;
    playerStatus: 'active' | 'completed';
    token: string;
  } | null>(null);

  // Update <title> + Open Graph / Twitter meta tags for social sharing.
  useAdventureMetaTags({ adventure: adventure as AdventureDetail });

  // Signal to the SEO pre-renderer (Puppeteer) that the page is ready to capture.
  // We delay slightly so the useAdventureMetaTags hook (which mutates <head>
  // directly) has finished committing its DOM changes before capture.
  useEffect(() => {
    if (adventure !== null || error !== null) {
      const timer = setTimeout(() => {
        document.dispatchEvent(new Event('seo:ready'));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [adventure, error]);

  useEffect(() => {
    trackPageVisit();
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await gameApi.getAdventureBySlug(slug);
        if (!cancelled) {
          setAdventure(data.adventure);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[AdventureDetailPage] Failed to load adventure:', err);
          setError(err instanceof Error ? err.message : 'Erro a carregar a aventura');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Banner server-backed (F3): no mount, se houver cookie com token, chama-se
  // o /resume para obter o progresso actual. Se o servidor renovar o token
  // (estava expirado), regrava-se o cookie. Fallback para loadSession() só
  // quando não há cookie.
  useEffect(() => {
    let cancelled = false;
    const deviceToken = loadDeviceToken();
    if (!deviceToken) {
      // Sem cookie — fallback para o snapshot de localStorage (display only).
      const saved = loadSession();
      if (saved) {
        setResumeSession({
          adventureId: saved.adventureId ?? '',
          adventureTitle: saved.adventureTitle,
          currentEpisode: saved.currentEpisode,
          totalEpisodes: saved.totalEpisodes,
          playerStatus: saved.playerStatus === 'completed' ? 'completed' : 'active',
          token: saved.token,
        });
      }
      return;
    }
    (async () => {
      try {
        const result = await gameApi.resume(deviceToken);
        if (cancelled) return;
        if (result.status === 'not_found') return;
        // Se o servidor renovou o token, regrava o cookie.
        if (result.token) {
          saveDeviceToken(result.token);
        }
        if (!result.adventureId || !result.adventureTitle) return;
        setResumeSession({
          adventureId: result.adventureId,
          adventureTitle: result.adventureTitle,
          currentEpisode: result.currentEpisode ?? 1,
          totalEpisodes: result.totalEpisodes ?? 0,
          playerStatus: (result.playerStatus ?? 'active') as 'active' | 'completed',
          token: result.token ?? deviceToken,
        });
      } catch (err) {
        // Erro de rede/servidor — ignora silenciosamente (o banner não é crítico).
        console.warn('[AdventureDetailPage] resume failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold text-dark mb-2">Aventura não encontrada</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors"
          >
            Voltar ao catálogo
          </button>
        </div>
      </div>
    );
  }

  if (!adventure) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Banner server-backed: só mostramos "Continuar" se o run do cookie for
  // desta aventura exacta.
  const isCurrentAdventure = resumeSession?.adventureId === adventure.id;

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFBF5]">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/" className="inline-block">
            <img src={logoSvg} alt="Selna" className="w-32 sm:w-36 md:w-40 h-auto" />
          </Link>
        </div>
      </header>

      <AdventureDetailHero adventure={adventure} />

      {/* Critic quote — só mostra se a aventura já foi avaliada */}
      {adventure.criticQuote && (
        <section className="px-4 py-8 md:py-12 bg-gray-50">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-4xl text-accent mb-2">"</div>
            <p className="text-xl md:text-2xl font-serif italic text-dark mb-4">
              {adventure.criticQuote}
            </p>
            <p className="text-sm text-gray-500">
              Avaliação do crítico IA
            </p>
          </div>
        </section>
      )}

      {/* Synopsis — always shown */}
      {adventure.synopsis && (
        <section className="px-4 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-dark mb-4">
              Sobre a história
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
              {adventure.synopsis}
            </p>
          </div>
        </section>
      )}

      {/* If user already plays this exact adventure, show "Continue" between
          the synopsis and the cast list. */}
      {isCurrentAdventure && resumeSession && (
        <section className="px-4 mb-8">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-6 text-center">
            <p className="text-sm text-gray-500 mb-1">A tua aventura</p>
            <p className="text-lg font-bold text-dark mb-4">{resumeSession.adventureTitle}</p>
            <button
              onClick={() => {
                const { token, playerStatus } = resumeSession;
                if (playerStatus === 'completed') navigate(`/complete/${token}`);
                else navigate(`/play/${token}`);
              }}
              className="w-full px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-xl shadow-md hover:shadow-lg transform hover:scale-[1.01] transition-all duration-200"
            >
              {resumeSession.playerStatus === 'completed' ? 'Ver resultados' : 'Continuar a jogar'}
            </button>
          </div>
        </section>
      )}

      {/* Cast — always shown */}
      <AdventureCastList adventure={adventure} />

      {/* Start form (anonymous) — only when the user hasn't started this
          adventure yet. */}
      {!isCurrentAdventure && (
        <section className="px-4 py-12 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-dark mb-3">
              Pronto para começar?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Sem registo. Começa a jogar já — só pedimos o email mais
              tarde, para sincronizar entre dispositivos.
            </p>
            <div className="flex justify-center">
              <StartAdventureForm
                adventureId={adventure.id}
                adventureTitle={adventure.title}
              />
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-400 border-t border-gray-100">
        <p>
          © {new Date().getFullYear()} Selna ·{' '}
          <a href="https://selna.ai" className="text-primary hover:underline">
            selna.ai
          </a>
        </p>
      </footer>
    </div>
  );
}
